import { Reports } from "@domains/reports/pages";
import { AdminReports } from "@domains/reports/pages/admin/AdminReports";
import { StipendsPage } from "@domains/stipends/pages/employee/StipendsPage";
import { StipendsAdmin } from "@domains/stipends/pages/admin/StipendsAdmin";
import { StatsPage } from "@domains/stats/StatsPage";
import { Navigate, Route, Routes } from "react-router-dom";
import { NotFoundPage, PageLayout } from "@common/components";
import { availableRoles, hasRole, roleState } from "@common/state/roleState";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  ExpenseReportStatus,
  StipendExpenseStatus,
  User,
  UserRole,
  useAdminAwxOrganizationStateQuery,
  useExpenseReportsByViewLazyQuery,
  useStipendExpensesByViewLazyQuery,
} from "@common/utils/graphql";
import { useEffect, useRef, useState } from "react";
import { SettingsPage } from "@domains/settings/SettingsPage";
import { from } from "@apollo/client";
import {
  sentryBreadcrumbsLink,
  authLink,
  retryLink,
  httpLink,
  tokenRefreshLink,
} from "@common/utils/graphql/apollo/links";
import { apollo } from "@common/utils/graphql/apollo/apollo";
import { FullPageLoader } from "@common/components/Loading/FullPageLoading";
import { AdminHomePage } from "@domains/home/pages/HomePage/AdminHomePage";
import { NonAdminHomePage } from "@domains/home/pages/HomePage/NonAdminHomePage";
import { AdminReportsSelectedTab } from "@domains/reports/pages/admin/useAdminReportsTabs";
import { AdminStipendsSelectedTab } from "@domains/stipends/pages/admin/useAdminStipendsTabs";
import { EmployeeReportsSelectedTab } from "@domains/reports/pages/employee/useEmployeeReportsTabs";
import { useUserStore } from "@domains/auth/utils/useUserStore";
import { EmployeeStipendsSelectedTab } from "@domains/stipends/pages/employee/useEmployeeStipendsTabs";
import { useAppNotification } from "@common/state/useAppNotifications";
import {
  badgeNumberState,
  roleHasBadges,
} from "@common/state/badgeNumberState";
import { SettingsSelectedTab } from "@domains/settings/constants";
import posthog from "posthog-js";
import { useCurrentUser } from "@common/hooks";
import { impersonatableUserState } from "@common/state/impersonsonatingUser";
import { awxOrganizationStateAtom } from "@common/state/organizationState";
import { LoggedInAuthRoutes } from "@domains/auth";

const useSyncBadgeNumbers = (role: UserRole) => {
  const setBadgeState = useSetRecoilState(badgeNumberState);
  const [fetchStipends] = useStipendExpensesByViewLazyQuery({
    variables: {
      stipendExpenseStatus: StipendExpenseStatus.Pending,
      offset: 0,
      pageSize: 21,
    },
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      if (
        data?.stipendExpensesByView?.__typename ===
          "QueryStipendExpensesByViewSuccess" &&
        roleHasBadges(role)
      ) {
        const length = data.stipendExpensesByView.data.stipendExpenses.length;
        setBadgeState((prevState) => ({
          ...prevState,
          [role]: {
            ...prevState[role],
            stipends: length,
          },
        }));
      }
    },
  });
  const [fetchReports] = useExpenseReportsByViewLazyQuery({
    variables: {
      offset: 0,
      pageSize: 21,
      expenseReportStatus: [ExpenseReportStatus.Pending],
      searchText: undefined,
    },
    fetchPolicy: "cache-and-network",
    onCompleted: (data) => {
      if (
        data?.expenseReportsByView?.__typename ===
          "QueryExpenseReportsByViewSuccess" &&
        roleHasBadges(role)
      ) {
        const length = data.expenseReportsByView.data.reports.length;
        setBadgeState((prevState) => ({
          ...prevState,
          [role]: {
            ...prevState[role],
            reports: length,
          },
        }));
      }
    },
  });

  useEffect(() => {
    if (roleHasBadges(role)) {
      fetchStipends();
      fetchReports();
    }
  }, [role, fetchReports, fetchStipends]);
};

export const AuthenticatedRoutes = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [role, setRole] = useRecoilState(roleState);
  const [roles, setRoles] = useRecoilState(availableRoles);
  const [impersonatableState, setImpersonatableUser] = useRecoilState(
    impersonatableUserState,
  );
  const { maybeCurrentUser, currentUserLoading } = useCurrentUser();
  const setUser = useUserStore((state) => state.setUser);
  const { fetchNotifications, loadingNotifications } = useAppNotification();
  const setAwxOrganizationState = useSetRecoilState(awxOrganizationStateAtom);

  // This is a way avoid adding a fn to a dependency array while ensuring the reference to it is never stale
  const fetchNotificationsRef = useRef(fetchNotifications);
  fetchNotificationsRef.current = fetchNotifications;

  // fetch notifications whenever role is changes
  useEffect(() => {
    fetchNotificationsRef.current();
  }, [role]);

  // update role state based on route when app loads
  useEffect(() => {
    if (currentUserLoading || roles.length === 0) {
      return;
    }

    const isAdminRoute =
      location.pathname.includes("/admin") && hasRole(UserRole.Admin, roles);
    const isManagerRoute =
      location.pathname.includes("/manager") &&
      hasRole(UserRole.Manager, roles);

    if (isAdminRoute) {
      setRole(UserRole.Admin);
    } else if (isManagerRoute) {
      setRole(UserRole.Manager);
    }

    setIsLoading(false);
  }, [currentUserLoading, roles, setRole]);

  // update auth link to include role as a header
  useEffect(() => {
    if (!apollo) {
      return;
    }
    apollo.setLink(
      from([
        sentryBreadcrumbsLink,
        retryLink,
        authLink(
          role,
          impersonatableState.impersonatingUser?.employee?.employeeId ?? "",
        ),
        tokenRefreshLink,
        httpLink,
      ]),
    );
  }, [role, impersonatableState.impersonatingUser?.employee?.employeeId]);

  useEffect(() => {
    if (!maybeCurrentUser) {
      return;
    }

    const { firstName, lastName, employee, cardholder, userId, displayName } =
      maybeCurrentUser;
    const workEmail = employee?.workEmail || null;
    const personalEmail = cardholder?.personalEmail || null;
    const residentialAddress = cardholder?.residentialAddress || null;
    const isBankAccountLinked = employee?.awxBeneficiaryId !== null;
    const employeeId = employee?.employeeId || null;
    const departmentId = employee?.department?.id || null;
    const homePageCardImageUrl =
      employee?.organization?.homePageCardImageUrl || null;
    const cardApplicationPageUrl =
      employee?.organization?.cardApplicationPageUrl || null;
    const organizationName = employee?.organization?.name || null;
    const organizationId = employee?.organization?.organizationId || null;
    setImpersonatableUser((prev) => ({
      ...prev,
      impersonatableUsers: (employee?.impersonatableDelegateUsers ||
        []) as User[],
    }));

    posthog.identify(userId, { workEmail });
    setRoles(maybeCurrentUser.roles);
    setUser({
      firstName,
      lastName,
      workEmail,
      isBankAccountLinked,
      personalEmail,
      residentialAddress,
      userId,
      employeeId,
      displayName,
      departmentId,
      homePageCardImageUrl,
      cardApplicationPageUrl,
      organizationName,
      organizationId,
    });
  }, [maybeCurrentUser, setImpersonatableUser, setRoles, setUser]);

  const { data: organizationStateData } = useAdminAwxOrganizationStateQuery();

  const awxOrganizationState =
    organizationStateData?.awxOrganizationState?.__typename ===
    "QueryAwxOrganizationStateSuccess"
      ? organizationStateData.awxOrganizationState.data
      : null;

  useEffect(() => {
    if (awxOrganizationState) {
      setAwxOrganizationState(awxOrganizationState);
    }
  }, [awxOrganizationState, setAwxOrganizationState]);

  const isAdmin = role === UserRole.Admin;
  const isManager = role === UserRole.Manager;
  const isEmployee = role === UserRole.Employee;

  useSyncBadgeNumbers(role);
  if (
    currentUserLoading ||
    isLoading ||
    roles.length === 0 ||
    loadingNotifications
  ) {
    return <FullPageLoader className="h-[100vh]" />;
  }

  return (
    <Routes>
      {/* Auth Routes: Unauthorized, Sign Out */}
      {LoggedInAuthRoutes}

      <Route element={<PageLayout />}>
        {/* Reports */}
        {isAdmin && (
          <>
            <Route
              path={"/admin/reports/pending"}
              element={<AdminReports tab={AdminReportsSelectedTab.Pending} />}
            />
            <Route
              path={"/admin/reports/history"}
              element={<AdminReports tab={AdminReportsSelectedTab.History} />}
            />
            <Route
              path={"/admin/reports/policy-manager"}
              element={
                <AdminReports tab={AdminReportsSelectedTab.PolicyManager} />
              }
            />
            <Route
              path="/:role?/reports/*"
              element={<Navigate to="/admin/reports/pending" />}
            />
          </>
        )}
        {isManager && (
          <>
            <Route
              path={"/manager/reports/pending"}
              element={<AdminReports tab={AdminReportsSelectedTab.Pending} />}
            />
            <Route
              path={"/manager/reports/history"}
              element={<AdminReports tab={AdminReportsSelectedTab.History} />}
            />
            <Route
              path="/:role?/reports/*"
              element={<Navigate to="/manager/reports/pending" />}
            />
          </>
        )}
        {isEmployee && (
          <>
            <Route
              path="/reports/draft"
              element={<Reports tab={EmployeeReportsSelectedTab.Draft} />}
            />
            <Route
              path="/reports/pending"
              element={<Reports tab={EmployeeReportsSelectedTab.Pending} />}
            />
            <Route
              path="/reports/resolved"
              element={<Reports tab={EmployeeReportsSelectedTab.Resolved} />}
            />
            <Route
              path="/:role?/reports/*"
              element={<Navigate to="/reports/draft" />}
            />
          </>
        )}
        {/* Stipends */}
        {isAdmin && (
          <>
            <Route
              path="/admin/stipends/pending"
              element={<StipendsAdmin tab={AdminStipendsSelectedTab.Pending} />}
            />
            <Route
              path="/admin/stipends/history"
              element={<StipendsAdmin tab={AdminStipendsSelectedTab.History} />}
            />
            <Route
              path="/admin/stipends/stipend-manager"
              element={
                <StipendsAdmin tab={AdminStipendsSelectedTab.StipendManager} />
              }
            />
            <Route
              path="/:role?/stipends/*"
              element={<Navigate to="/admin/stipends/pending" />}
            />
          </>
        )}
        {isManager && (
          <>
            <Route
              path="/manager/stipends/pending"
              element={<StipendsAdmin tab={AdminStipendsSelectedTab.Pending} />}
            />
            <Route
              path="/manager/stipends/history"
              element={<StipendsAdmin tab={AdminStipendsSelectedTab.History} />}
            />
            <Route
              path="/:role?/stipends/*"
              element={<Navigate to="/manager/stipends/pending" />}
            />
          </>
        )}
        {isEmployee && (
          <>
            <Route
              path="/stipends/active"
              element={
                <StipendsPage tab={EmployeeStipendsSelectedTab.Active} />
              }
            />
            <Route
              path="/stipends/inactive"
              element={
                <StipendsPage tab={EmployeeStipendsSelectedTab.Inactive} />
              }
            />
            <Route
              path="/stipends/upcoming"
              element={
                <StipendsPage tab={EmployeeStipendsSelectedTab.Upcoming} />
              }
            />
            <Route
              path="/:role?/stipends/*"
              element={<Navigate to="/stipends/active" />}
            />
          </>
        )}
        {/* Stats */}
        {isAdmin && (
          <>
            <Route path="/admin/stats" element={<StatsPage />} />
            <Route
              path="/:role?/stats/*"
              element={<Navigate to="/admin/stats" />}
            />
          </>
        )}

        {/* Settings */}
        {isAdmin && (
          <>
            <Route
              path="/admin/settings/people"
              element={<SettingsPage tab={SettingsSelectedTab.People} />}
            />
            <Route
              path="/admin/settings/departments"
              element={<SettingsPage tab={SettingsSelectedTab.Departments} />}
            />
            <Route
              path="/admin/settings/categories"
              element={<SettingsPage tab={SettingsSelectedTab.Categories} />}
            />
            <Route
              path="/admin/settings/bank-account"
              element={<SettingsPage tab={SettingsSelectedTab.BankAccount} />}
            />
            <Route
              path="/admin/settings/custom-fields"
              element={<SettingsPage tab={SettingsSelectedTab.CustomFields} />}
            />
            <Route
              path="/:role?/settings/bank-account"
              element={<Navigate to="/admin/settings/bank-account" />}
            />
            <Route
              path="/:role?/settings/*"
              element={<Navigate to="/admin/settings/people" />}
            />
          </>
        )}
        {isManager && (
          <>
            <Route
              path="/manager/settings/people"
              element={<SettingsPage tab={SettingsSelectedTab.People} />}
            />
            <Route
              path="/:role?/settings/*"
              element={<Navigate to="/manager/settings/people" />}
            />
          </>
        )}
        {isEmployee && (
          <>
            <Route
              path="/settings/profile"
              element={<SettingsPage tab={SettingsSelectedTab.Profile} />}
            />
            <Route
              path="/settings/bank-account"
              element={<SettingsPage tab={SettingsSelectedTab.BankAccount} />}
            />
            <Route
              path="/:role?/settings/*"
              element={<Navigate to="/settings/profile" />}
            />
          </>
        )}
        {/* Home Page */}
        {isAdmin ? (
          <>
            <Route path="/admin/home" element={<AdminHomePage />} />
            <Route path="*" element={<Navigate to="/admin/home" />} />
          </>
        ) : (
          <>
            <Route path="/home" element={<NonAdminHomePage />} />
            <Route path="*" element={<Navigate to="/home" />} />
          </>
        )}
      </Route>
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};
